import React, { useState, useRef, useEffect, useCallback } from 'react';
import './M3Progress.css';

/**
 * M3 Linear Progress — indeterminate + determinate.
 *
 * @param {number} [value] — 0–100 for determinate mode
 * @param {boolean} [indeterminate] — show animated indeterminate state
 * @param {string} [variant='primary'|'secondary'|'tertiary'|'inverse']
 */
export function M3LinearProgress({ value = 0, indeterminate = false, variant = 'primary' }) {
  return (
    <div className={`m3-linear-progress m3-linear-progress-${variant}`} role="progressbar">
      {!indeterminate && (
        <div
          className="m3-linear-progress-bar"
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      )}
      {indeterminate && (
        <>
          <div className="m3-linear-progress-indeterminate-track" />
          <div className="m3-linear-progress-indeterminate-primary" />
          <div className="m3-linear-progress-indeterminate-secondary" />
        </>
      )}
    </div>
  );
}

/**
 * M3 Circular Progress.
 *
 * @param {number} [value] — 0–100 for determinate
 * @param {boolean} [indeterminate]
 * @param {string} [size] — 'small' (24px) | 'default' (40px) | 'large' (56px)
 */
export function M3CircularProgress({ value = 0, indeterminate = false, size = 'default' }) {
  const radius = size === 'small' ? 10 : size === 'large' ? 24 : 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = indeterminate
    ? 0
    : circumference * (1 - Math.min(100, Math.max(0, value)) / 100);

  const sizeMap = { small: 24, default: 40, large: 56 };
  const strokeWidth = size === 'small' ? 3 : size === 'large' ? 3.6 : 3.6;

  return (
    <div className={`m3-circular-progress m3-circular-progress-${size}`} role="progressbar">
      <svg viewBox="0 0 40 40" width={sizeMap[size]} height={sizeMap[size]}>
        <circle
          cx="20"
          cy="20"
          r={radius}
          className="m3-circular-progress-track"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx="20"
          cy="20"
          r={radius}
          className="m3-circular-progress-indicator"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={indeterminate ? circumference * 0.65 : strokeDashoffset}
          style={{
            transformOrigin: 'center',
            transform: indeterminate ? 'rotate(-90deg)' : undefined,
            animation: indeterminate ? 'circular-spin 1.4s linear infinite' : undefined
          }}
        />
      </svg>
    </div>
  );
}

export default M3LinearProgress;
